import React, { useState } from 'react';
import { CircleDashed, CheckCircle, Cpu, ArrowsClockwise, Wrench, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DASHBOARD } from '@/constants/testIds';
import OperationalStatusBadge from '@/components/OperationalStatusBadge';
import { OPERATIONAL_STATUS } from '@/constants/operationalTruth';

const getLogTypeColor = (type) => {
  if (type === 'system') return 'text-[#00E5FF]';
  if (type === 'error') return 'text-[#F85149]';
  if (type === 'success') return 'text-[#10B981]';
  return 'text-[#F3F4F6]';
};

const ProductionAIPanel = ({
  session,
  messages = [],
  onConnect,
  onDisconnect,
  onReplace,
  extraLogs = [],
  handoffPackage = null,
  activeExecution,
  onCancelExecution,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [operationLogs, setOperationLogs] = useState([]);
  const isConnected = session?.production_ai?.status === 'connected';
  const isRealVerified = session?.production_ai?.mode === 'api' && session?.production_ai?.verified === true;

  const productionMessages = (messages || [])
    .map(m => ({
      id: m.id || m._id || `${m.timestamp}-${m.content || m.text}`,
      source: m.source || m.sender,
      content: m.content || m.text,
      timestamp: m.timestamp
    }))
    .filter(m => m.source === 'production_ai');

  const uniqueProductionMessages = [];
  const seenIds = new Set();
  for (const msg of productionMessages) {
    if (msg.id && !seenIds.has(msg.id)) {
      seenIds.add(msg.id);
      uniqueProductionMessages.push(msg);
    }
  }

  const getExecutionStatusText = (status) => {
    switch (status) {
      case 'accepting': return 'Preparando mensaje...';
      case 'pending': return 'Mensaje aceptado y en espera.';
      case 'running': return 'Procesando respuesta.';
      case 'completed': return 'Respuesta IA incorporada una sola vez a la conversación.';
      case 'failed': return `Ejecución fallida: ${activeExecution?.error_message || 'Error desconocido'}`;
      case 'timed_out': return 'La ejecución excedió el tiempo permitido.';
      case 'cancel_requested': return 'Cancelación solicitada.';
      case 'cancelled': return 'Ejecución cancelada.';
      default: return 'Ejecutando...';
    }
  };

  // Render Collapsed Strip view
  if (isCollapsed) {
    return (
      <div 
        data-testid={DASHBOARD.productionPanel}
        onClick={onToggleCollapse}
        className="h-full w-full bg-[#0E1117] border-l border-white/[0.08] flex flex-col items-center py-3 cursor-pointer hover:border-white/[0.15] transition-all select-none group gap-3"
        title="Expandir Panel Production AI"
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse();
          }}
          className="p-1.5 hover:bg-white/[0.06] text-slate-400 hover:text-white rounded-md transition-all"
        >
          <CaretLeft size={14} />
        </button>
        <div className="flex-1 flex flex-col items-center gap-2">
          <Cpu size={16} className="text-slate-400 group-hover:text-cyan-400 transition-colors" />
          <span
            data-testid="production-status-dot"
            className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-slate-600'}`}
            title={isConnected ? 'Connected' : 'Disconnected'}
          />
        </div>
      </div>
    );
  }

  const combinedLogs = [...operationLogs, ...extraLogs];
  
  const handleConnect = async () => {
    await onConnect('production');
    setOperationLogs([{
      id: Date.now(),
      type: 'system',
      content: '[CONNECTED] Demo Production AI initialized',
      timestamp: new Date().toISOString()
    }]);
  };
  
  return (
    <div 
      data-testid={DASHBOARD.productionPanel}
      className="h-full border-l border-[#1F2937] flex flex-col bg-[#0A0C10] overflow-hidden min-h-0"
    >
      {/* Header */}
      <div className="p-4 border-b border-[#1F2937] flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-sm font-bold terminal-font text-[#F3F4F6] uppercase tracking-wider">Production AI</h2>
          <div className="flex items-center gap-2 mt-1">
            {isConnected ? (
              <CheckCircle size={14} className="text-[#10B981] pulse-glow" weight="fill" />
            ) : (
              <CircleDashed size={14} className="text-[#9CA3AF]" />
            )}
            <span className={`text-xs terminal-font font-bold ${isConnected ? 'text-[#10B981]' : 'text-[#9CA3AF]'}`}>
              {isConnected ? 'CONNECTED' : 'DISCONNECTED'}
            </span>
            {isConnected && (
              isRealVerified ? (
                <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} label="REAL / VERIFIED" size="sm" />
              ) : (
                <OperationalStatusBadge status={OPERATIONAL_STATUS.DEMO} label="DEMO CONNECTED" size="sm" />
              )
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="p-1 hover:bg-[#1C1C24] text-[#9CA3AF] hover:text-[#10B981] rounded transition-colors"
              title="Colapsar Panel IA de Producción"
            >
              <CaretRight size={16} />
            </button>
          )}
        </div>
      </div>

      {isConnected && session?.production_ai && (
        <div className="px-4 py-2 bg-[#13131A] border-b border-[#1F2937] text-xs body-font text-[#9CA3AF] flex items-center justify-between shrink-0">
          <div>Provider: <span className="text-[#F3F4F6]">{session.production_ai.name}</span></div>
          <div>Capabilities: <span className="text-[#F3F4F6]">Code, UI, Testing</span></div>
        </div>
      )}

      {/* Active Execution Banner */}
      {activeExecution && (
        <div className="p-2 border-b border-[#1F2937] bg-[#1C1C24] flex items-center justify-between shrink-0">
          <span className="text-xs terminal-font text-[#00E5FF]">
            Estado: {getExecutionStatusText(activeExecution.status)}
          </span>
          {activeExecution.cancellable && activeExecution.status !== 'cancel_requested' && !['completed', 'failed', 'timed_out', 'cancelled'].includes(activeExecution.status) && (
            <Button
              size="sm"
              variant="outline"
              onClick={onCancelExecution}
              className="h-6 text-[10px] border-[#F85149] text-[#F85149] hover:bg-[#F85149]/10 terminal-font flex items-center justify-center"
            >
              Cancelar
            </Button>
          )}
        </div>
      )}
      
      {/* Operations Logs Section */}
      <div className="flex-1 flex flex-col min-h-0 p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3 shrink-0">
          <h3 className="text-xs font-bold terminal-font text-[#9CA3AF] uppercase tracking-wider">
            Registros de Operaciones
          </h3>
          {isConnected && (
            <Button
              data-testid={DASHBOARD.productionReplaceBtn}
              onClick={onReplace}
              variant="outline"
              className="border-[#D97706] text-[#D97706] hover:bg-[#D97706]/10 terminal-font text-[10px] h-6"
            >
              <ArrowsClockwise size={10} className="mr-1" />
              Reemplazar IA (2-Fases)
            </Button>
          )}
        </div>
        
        <ScrollArea className="flex-1 border border-[#1F2937] bg-[#0A0C10] p-3 min-h-0">
          <div className="space-y-2 pr-2">
            {!isConnected ? (
              <div className="text-center py-6">
                <CircleDashed size={24} className="mx-auto mb-3 text-[#9CA3AF] animate-spin" />
                <p className="text-xs text-[#9CA3AF] body-font mb-4">No hay conexión de IA de producción</p>
                <Button 
                  data-testid={DASHBOARD.productionConnectBtn}
                  onClick={handleConnect}
                  className="bg-[#10B981] text-[#0A0C10] hover:bg-[#10B981]/90 terminal-font text-xs font-bold"
                >
                  Conectar la IA de producción
                </Button>
              </div>
            ) : combinedLogs.length > 0 ? (
              combinedLogs.map((log) => (
                <div key={log.id} className="text-xs terminal-font space-y-1 p-2 bg-[#13131A] border border-[#1F2937]">
                  <div className="flex items-center justify-between text-[10px] text-[#9CA3AF]">
                    <span className={getLogTypeColor(log.type)}>
                      [{log.type.toUpperCase()}]
                    </span>
                    <span>{new Date(log.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <p className="text-[#F3F4F6] body-font whitespace-pre-wrap">{log.content}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-6">
                <Wrench size={24} className="mx-auto mb-3 text-[#9CA3AF]" />
                <p className="text-xs text-[#9CA3AF] body-font">Esperando operaciones...</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Production AI Response Section */}
      {uniqueProductionMessages.length > 0 && (
        <div
          data-testid="production-ai-response-container"
          className="p-3 border-t border-[#10B981] bg-[#13131A] space-y-2 shrink-0 max-h-[160px] flex flex-col min-h-0"
        >
          <div className="flex items-center justify-between shrink-0">
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#10B981] terminal-font font-bold">
                Production AI Response
              </span>
              <span className="text-[9px] text-[#9CA3AF] font-mono mt-0.5">
                Identity: {session?.production_ai?.name || 'Production AI'}
              </span>
            </div>
            <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} size="sm" label="REAL RESPONSE" />
          </div>
          <div className="space-y-2 max-h-[110px] overflow-y-auto pr-1 flex-1 min-h-0">
            {uniqueProductionMessages.map((msg) => (
              <div
                key={msg.id}
                className="text-xs terminal-font p-2 bg-[#0A0C10] border border-[#1F2937] rounded space-y-1"
              >
                <p className="text-[#F3F4F6] font-mono whitespace-pre-wrap">{msg.content}</p>
                {msg.timestamp && (
                  <div className="text-[9px] text-[#9CA3AF] text-right">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Handoff Package Summary */}
      {handoffPackage && (
        <div 
          data-testid={DASHBOARD.handoffPackageSummary}
          className="p-3 border-t border-[#D97706] bg-[#13131A] space-y-1 shrink-0 max-h-[160px] flex flex-col min-h-0"
        >
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <ArrowsClockwise size={12} className="text-[#D97706]" weight="bold" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#D97706] terminal-font font-bold">
                Handoff Package
              </span>
            </div>
            <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} size="sm" label="REAL PERSISTED" />
          </div>
          <div className="text-xs terminal-font text-[#F3F4F6] leading-relaxed max-h-[110px] overflow-y-auto pr-1 flex-1 min-h-0">
            {handoffPackage.status === 'transferred' ? (
              <>
                <div className="text-[#10B981] font-bold mb-1">HANDOFF RECEIVED / TRANSFERRED</div>
                <div className="text-[#9CA3AF]">ID: <span className="text-[#F3F4F6]">{handoffPackage.id || handoffPackage.handoff_id}</span></div>
                <div className="text-[#9CA3AF]">Source: <span className="text-[#F3F4F6]">{handoffPackage.from_ai || 'Origin AI'}</span></div>
                <div className="text-[#9CA3AF]">Execution ID: <span className="text-[#F3F4F6]">{handoffPackage.execution_id}</span></div>
                <div className="text-[#9CA3AF] mt-1">Summary: <span className="text-[#F3F4F6] block mt-0.5 whitespace-pre-wrap">{handoffPackage.instruction}</span></div>
              </>
            ) : (
              <>
                <div className="text-[#9CA3AF]">From: <span className="text-[#F3F4F6]">{handoffPackage.from_ai}</span></div>
                <div className="text-[#9CA3AF]">To: <span className="text-[#F3F4F6]">{handoffPackage.to_ai}</span></div>
                <div className="text-[#9CA3AF]">Phase: <span className="text-[#00E5FF]">{handoffPackage.current_phase}</span></div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Quick Stats */}
      {session?.project && (
        <div className="p-3 border-t border-[#1F2937] space-y-1 text-xs shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-[#9CA3AF] terminal-font">Archivos Creados:</span>
            <span className="text-[#10B981] terminal-font font-bold">{session.project.files_created?.length || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#9CA3AF] terminal-font">Archivos Modificados:</span>
            <span className="text-[#D97706] terminal-font font-bold">{session.project.files_modified?.length || 0}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionAIPanel;